import type {
  LanguageOptionsDto,
  SelectedLanguageDto,
} from '@nomiss/sdk';
import {
  ApiRequestError,
  executeSdkRawRequest,
} from './api-request.util';
import { usersApi } from './sdk-client';

export type LanguageItem = {
  id: string;
  code: string;
  englishName: string;
  nativeName: string;
  minTier?: string;
};

export type LanguageGroup = {
  label: string;
  minTier: string;
  unlocked: boolean;
  items: LanguageItem[];
};

export type LanguageOptionsResponse = {
  tier: string;
  selectedId: string;
  groups: LanguageGroup[];
};

export type SelectedLanguage = {
  id: string;
  code: string;
  englishName: string;
  nativeName: string;
  minTier: string;
};

export class LanguageError extends ApiRequestError {
  constructor(status: number, message: string) {
    super(status, message, 'LanguageError');
  }
}

function normalizeLanguageItem(item: {
  id: string;
  code: string;
  englishName: string;
  nativeName?: unknown;
  minTier?: string;
}): LanguageItem {
  return {
    id: item.id,
    code: item.code,
    englishName: item.englishName,
    nativeName:
      typeof item.nativeName === 'string'
        ? item.nativeName
        : item.englishName,
    ...(item.minTier !== undefined ? { minTier: item.minTier } : {}),
  };
}

function normalizeLanguageOptions(
  payload: LanguageOptionsDto,
): LanguageOptionsResponse {
  return {
    tier: payload.tier,
    selectedId: payload.selectedId,
    groups: (payload.groups ?? []).map(group => ({
      label: group.label,
      minTier: group.minTier,
      unlocked: group.unlocked,
      items: (group.items ?? []).map(normalizeLanguageItem),
    })),
  };
}

function normalizeSelectedLanguage(payload: SelectedLanguageDto): SelectedLanguage {
  return {
    id: payload.id,
    code: payload.code,
    englishName: payload.englishName,
    nativeName:
      typeof payload.nativeName === 'string'
        ? payload.nativeName
        : payload.englishName,
    minTier: payload.minTier,
  };
}

export async function getSelectedLanguage(): Promise<SelectedLanguage> {
  const data = await executeSdkRawRequest<SelectedLanguageDto>({
    request: () => usersApi.languagePickerControllerGetSelectedV1Raw({}),
    fallback: 'Failed to load selected language',
    ErrorClass: LanguageError,
  });

  return normalizeSelectedLanguage(data);
}

export async function getLanguageOptions(): Promise<LanguageOptionsResponse> {
  const data = await executeSdkRawRequest<LanguageOptionsDto>({
    request: () => usersApi.languagePickerControllerOptionsV1Raw({}),
    fallback: 'Failed to load language options',
    ErrorClass: LanguageError,
  });

  return normalizeLanguageOptions(data);
}

export async function updateLanguage(languageId: string): Promise<SelectedLanguage> {
  const data = await executeSdkRawRequest<SelectedLanguageDto>({
    request: () =>
      usersApi.languagePickerControllerUpdateV1Raw({
        updateLanguageDto: { languageId },
      }),
    fallback: 'Failed to update language',
    ErrorClass: LanguageError,
  });

  return normalizeSelectedLanguage(data);
}

export function findSelectedLanguageLabel(
  response: LanguageOptionsResponse,
): string | null {
  for (const group of response.groups) {
    const selected = group.items.find(item => item.id === response.selectedId);
    if (selected) {
      return selected.nativeName || selected.englishName;
    }
  }

  return null;
}
